<template>
  <div class="space-y-6">
    <div class="bg-white rounded-lg shadow p-6">
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-xl font-bold">Vehicles</h2>
        <button @click="openForm" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          ➕ New Vehicle
        </button>
      </div>

      <div v-if="masterDataStore.loading" class="text-center py-12">
        <div class="inline-block spinner w-8 h-8"></div>
      </div>
      <div v-else-if="masterDataStore.vehicles.length === 0" class="text-center py-12 text-gray-500">
        No vehicles found
      </div>
      <div v-else class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-100 border-b">
            <tr>
              <th class="px-6 py-3 text-left text-sm font-semibold">ID</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Vehicle No</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Model</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">District</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Description</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Status</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y">
            <tr v-for="vehicle in masterDataStore.vehicles" :key="vehicle.id" class="hover:bg-gray-50">
              <td class="px-6 py-4 text-sm text-gray-500">{{ vehicle.id }}</td>
              <td class="px-6 py-4 text-sm font-medium">{{ vehicle.vehicle_no }}</td>
              <td class="px-6 py-4 text-sm">{{ vehicle.model }}</td>
              <td class="px-6 py-4 text-sm">{{ getDistrictName(vehicle.district_id) }}</td>
              <td class="px-6 py-4 text-sm">{{ vehicle.description }}</td>
              <td class="px-6 py-4 text-sm">
                <span :class="[
                  'px-2 py-1 rounded text-xs',
                  String(vehicle.is_active) === '1' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                ]">
                  {{ String(vehicle.is_active) === '1' ? 'Active' : 'Inactive' }}
                </span>
              </td>
              <td class="px-6 py-4 text-sm space-x-2">
                <button @click="editVehicle(vehicle)" class="text-blue-600 hover:text-blue-800">Edit</button>
                <button @click="deleteVehicleFunc(vehicle.id)" class="text-red-600 hover:text-red-800">Delete</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Form Modal -->
    <div v-show="showForm" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-xl p-6 w-96 max-h-[80vh] overflow-y-auto z-50 relative">
        <h3 class="text-lg font-bold mb-4">{{ editingVehicle ? 'Edit Vehicle' : 'New Vehicle' }}</h3>
        <form @submit.prevent="submitForm" class="space-y-4">
          <div>
            <label class="block text-sm font-medium mb-1">Vehicle No</label>
            <input v-model="formData.vehicle_no" placeholder="e.g., KL-01-AX-1234" class="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600" required />
          </div>
          <div>
            <label class="block text-sm font-medium mb-1">Model</label>
            <input v-model="formData.model" placeholder="e.g., Tata Ace" class="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600" required />
          </div>
          <div>
            <label class="block text-sm font-medium mb-1">District</label>
            <select v-model="formData.district_id" class="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600" required>
              <option value="">Select District</option>
              <option v-for="district in masterDataStore.districts" :key="district.id" :value="district.id">
                {{ district.name }}
              </option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium mb-1">Description</label>
            <input v-model="formData.description" placeholder="Enter description" class="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600" />
          </div>
          
          <div class="flex items-center">
            <input v-model="formData.is_active" type="checkbox" id="vehicle-active" class="w-4 h-4" />
            <label for="vehicle-active" class="ml-2 text-sm">Active</label>
          </div>
          <div class="flex justify-end gap-2 pt-4 border-t">
            <button type="button" @click="closeForm" class="px-4 py-2 border rounded hover:bg-gray-50">Cancel</button>
            <button type="submit" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">{{ editingVehicle ? 'Update' : 'Create' }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Swal from 'sweetalert2'
import { useMasterDataStore } from '@/stores/masterDataStore'

const masterDataStore = useMasterDataStore()
const showForm = ref(false)
const editingVehicle = ref(null)
const formData = ref({ vehicle_no: '', model: '', district_id: '', description: '', is_active: true })

const getDistrictName = (districtId) => {
  const d = masterDataStore.districts.find(d => String(d.id) === String(districtId))
  return d ? d.name : 'N/A'
}

const submitForm = async () => {
  try {
    if (!formData.value.vehicle_no || !formData.value.model || !formData.value.district_id) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Fields',
        text: 'Vehicle No, Model, and District are required',
        confirmButtonColor: '#3085d6'
      })
      return
    }
    const data = {
      vehicle_no: formData.value.vehicle_no,
      model: formData.value.model,
      district_id: Number(formData.value.district_id),
      description: formData.value.description || '',
      is_active: formData.value.is_active ? 1 : 0
    }
    if (editingVehicle.value) {
      await masterDataStore.updateVehicle(editingVehicle.value.id, data)
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Vehicle updated successfully',
        confirmButtonColor: '#3085d6'
      })
    } else {
      await masterDataStore.createVehicle(data)
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Vehicle created successfully',
        confirmButtonColor: '#3085d6'
      })
    }
    closeForm()
    await masterDataStore.getVehicles()
  } catch (error) {
    console.error('Form submission error:', error)
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: error.message || 'Failed to save',
      confirmButtonColor: '#3085d6'
    })
  }
}

const closeForm = () => {
  showForm.value = false
  editingVehicle.value = null
  formData.value = { vehicle_no: '', model: '', district_id: '', description: '', is_active: true }
}

const openForm = () => {
  console.log('Opening vehicle form...')
  showForm.value = true
}

const editVehicle = (vehicle) => {
  editingVehicle.value = vehicle
  formData.value = { ...vehicle }
  showForm.value = true
}

const deleteVehicleFunc = async (id) => {
  Swal.fire({
    title: 'Confirm Delete',
    text: 'Are you sure you want to delete this vehicle?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Yes, delete it',
    cancelButtonText: 'Cancel'
  }).then(async (result) => {
    if (result.isConfirmed) {
      await masterDataStore.deleteVehicle(id)
      Swal.fire({
        icon: 'success',
        title: 'Deleted',
        text: 'Vehicle deleted successfully',
        confirmButtonColor: '#3085d6'
      })
    }
  })
}

onMounted(async () => {
  await masterDataStore.getVehicles()
  await masterDataStore.getDistricts()
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

